import { maybe, rd } from '@passionware/monads';
import { SimpleReadOnlyEvent } from '@passionware/simple-event';
import { QueryClient, useQueries, useQuery } from '@tanstack/react-query';
import { Resource } from '__types__/v1/models';
import { throttle } from 'lodash';
import { Batcher, createBatcher } from '../../../../v4/entities/common/batcher';
import { V1Action } from 'v4/migration/actionAsEventMiddleware';
import {
  ResourceApi,
  ResourceListQuery,
  ResourceQuery
} from '../../../api/resource/resource.api';
import { asGlobalResourceQuery } from '../../../api/_common/query/workspace-scoping';
import { ensureIdleQuery } from '../../../platform/ensureIdleQuery';
import {
  rowLevelValidation,
  type ValidatedRow
} from '../../../platform/row-level-validation';
import { SchemaError } from 'v4/entities/common/parseWithDecoratedError';
import { WithMessageService } from '../../internal/MessageService/MessageService';
import { ResourceService } from './ResourceService';
import { WithErrorService } from '../../front/ErrorService/ErrorService';
import { WithServices } from '../../../platform/ts/services';

function normalizeResourceListQuery(query: ResourceListQuery): ResourceQuery {
  if ('workspaceId' in query) {
    return query;
  }
  return asGlobalResourceQuery(query);
}

/**
 * Cached in place of a contact that failed validation: the query keeps it as
 * data, so a refetch doesn't turn its readers back to pending, while
 * {@link unwrapCachedResource} still hands them the {@link SchemaError}.
 */
class InvalidResource {
  constructor(readonly error: SchemaError) {}
}

type CachedResource = Resource | InvalidResource;

function unwrapCachedResource(item: CachedResource): Resource {
  if (item instanceof InvalidResource) {
    throw item.error;
  }
  return item;
}

export function createResourceService(
  config: WithServices<[WithErrorService, WithMessageService]> & {
    api: ResourceApi;
    queryClient: QueryClient;
    onCacheDump: (resources: Resource[]) => void;
    v1Event: SimpleReadOnlyEvent<V1Action>;
  }
): ResourceService {
  const {
    api,
    queryClient,
    onCacheDump,
    v1Event,
    services: { errorService, messageService }
  } = config;
  const interceptor = errorService.withCategory('ResourceService');

  // Subscribe to system effects to handle cache invalidation on mutations
  messageService.reportSystemEffect.subscribeToRequest(async message => {
    for (const effect of message.request.scope) {
      switch (effect.entity) {
        case 'resource': {
          // Invalidate all resource list queries
          queryClient.removeQueries({
            queryKey: ['resources-v5', 'list'],
            type: 'inactive'
          });
          await queryClient.invalidateQueries({
            queryKey: ['resources-v5', 'list'],
            type: 'active'
          });
          // Invalidate the specific item query
          await queryClient.invalidateQueries({
            queryKey: ['resources-v5', 'item', effect.entityId]
          });
        }
      }
    }
    message.sendResponse();
  });

  const batcher: Batcher<
    Resource['id'],
    ValidatedRow<Resource>[],
    CachedResource
  > = createBatcher(
    async ids => {
      if (ids.length === 0) {
        return [];
      }
      return await interceptor.wrap(() => api.getItems(ids))();
    },
    (rows, id) => {
      const row = maybe.getOrThrow(
        maybe.of(rows.find(r => r.id === id)),
        `Resource with id ${id} not found`
      );
      if (row.isValid) {
        return row.data;
      }
      // Only this id fails; its batch-mates resolve as usual.
      const error = new SchemaError(row.error, row.rawItem, row.rawItem);
      errorService.reportError('ResourceService', error);
      return new InvalidResource(error);
    }
  );

  async function fetchItem(
    id: Resource['id'],
    signal: AbortSignal
  ): Promise<CachedResource> {
    const batched = await batcher.fetch({ payload: id, signal });
    if (!batched) {
      throw new Error(`Resource with id ${id} not found`);
    }
    if (!(batched instanceof InvalidResource)) {
      onCacheDump([batched]);
    }
    return batched;
  }

  const invalidateAll = throttle(
    async () => {
      queryClient.removeQueries({
        queryKey: ['resources-v5'],
        type: 'inactive'
      });
      await queryClient.invalidateQueries({
        queryKey: ['resources-v5'],
        type: 'active'
      });
    },
    // we don't want to invalidate the cache too often
    3000,
    {
      // after period of inactivity, we want to invalidate the cache immediately
      leading: true,
      // we want to make sure that last invalidation is executed
      trailing: true
    }
  );

  v1Event.addListener(async event => {
    switch (event.type) {
      case 'CREATE_CONTACT_SUCCESS':
      case 'UPDATE_CONTACT_SUCCESS':
      case 'ARCHIVE_CONTACT_SUCCESS':
      case 'UNARCHIVE_CONTACT_SUCCESS':
        await invalidateAll();
        return;
    }
  });

  // Subscribe to cache events for guest shortlist data
  messageService.reportCache.addListener(event => {
    for (const entity of event.entities) {
      switch (entity.entity) {
        case 'resource':
          queryClient.setQueryData(
            ['resources-v5', 'item', entity.payload.id],
            entity.payload
          );
          break;
      }
    }
  });

  return {
    useList: query =>
      ensureIdleQuery(
        query,
        useQuery(
          {
            queryKey: ['resources-v5', 'list', query],
            queryFn: interceptor.wrap(async ctx => {
              const normalizedQuery = normalizeResourceListQuery(query!);
              const result = await api.getList(normalizedQuery, ctx.signal);
              const validItems = rowLevelValidation.validDataFromRows(
                result.items
              );
              onCacheDump(validItems);
              for (const resource of validItems) {
                queryClient.setQueryData(
                  ['resources-v5', 'item', resource.id],
                  resource
                );
              }
              return result;
            }),
            enabled: maybe.isPresent(query)
          },
          queryClient
        )
      ),
    ensureList: query =>
      queryClient.fetchQuery({
        queryKey: ['resources-v5', 'list', query],
        queryFn: interceptor.wrap(ctx =>
          api
            .getList(normalizeResourceListQuery(query), ctx.signal)
            .then(value => {
              onCacheDump(rowLevelValidation.validDataFromRows(value.items));
              return value;
            })
        )
      }),
    useItem: id =>
      ensureIdleQuery(
        id,
        useQuery<CachedResource, Error, Resource>(
          {
            queryKey: ['resources-v5', 'item', id],
            queryFn: ({ signal }) => {
              if (!id) {
                throw new Error('Resource ID is required');
              }
              return fetchItem(id, signal);
            },
            select: unwrapCachedResource,
            enabled: maybe.isPresent(id)
          },
          queryClient
        )
      ),
    ensureItem: id =>
      queryClient
        .fetchQuery({
          queryKey: ['resources-v5', 'item', id],
          queryFn: ({ signal }): Promise<CachedResource> =>
            fetchItem(id, signal)
        })
        .then(unwrapCachedResource),
    useItemsById: ids =>
      rd.combineAll(
        useQueries({
          queries: ids.map(id => ({
            queryKey: ['resources-v5', 'item', id],
            queryFn: ({ signal }: { signal: AbortSignal }) =>
              fetchItem(id, signal),
            select: unwrapCachedResource
          }))
        })
      )
  };
}
